import { flow, isNull, unset } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { getAllDocumentCustomFilters, getUser } from '../../../auth/login/store/selectors';
import { DocumentQuery, MLData } from '../../../datasets/store/mlTypes';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { ClientAmendmentDocumentQuery, DocumentLinkDetails, DocumentsView, SuggestedAndRemainingQueries, UploadDocument } from './types';
import { AnalysisView, ArkDocument, ArkTableDocument, ArkTableDocumentEntity, LinkedDocument, SelectedDocument } from '../../store/types';
import { getMyDocumentsRoot } from '../../store/selectors';

export const getDocumentsView = (state: ApplicationState): DocumentsView => getMyDocumentsRoot(state).documentsView;

export const getUploadModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).uploadModalOpen;

export const getIsUploading = (state: ApplicationState): boolean => getMyDocumentsRoot(state).isUploading;

export const getIsLoading = (state: ApplicationState): boolean => getMyDocumentsRoot(state).isLoading;

export const getIsUpdatingDocument = (state: ApplicationState): boolean => getMyDocumentsRoot(state).isUpdatingDocumentDetails;

export const getFilesToUpload = (state: ApplicationState): File[] | null => getMyDocumentsRoot(state).filesToUpload;

export const getSelectedIncompleteDocuments = (state: ApplicationState): ArkDocument[] => getMyDocumentsRoot(state).selectedIncompleteDocuments;

export const getDocumentDetailsModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).documentDetailsModalOpen;

export const getOriginalTableDocuments = (state: ApplicationState): ArkTableDocument[] => {
    const documents = getMyDocumentsRoot(state).availableOriginalDocuments;

    const stripDocumentEntities = (document: ArkDocument) => flow(
        unset('clientId'),
        unset('createdDate'),
        unset('effectiveTo'),
        unset('isCurrent'),
        unset('version'),
        unset('entityAName'),
        unset('entityAMyCompany'),
        unset('entityAOnWatchlist'),
        unset('entityBName'),
        unset('entityBMyCompany'),
        unset('entityBOnWatchlist')
    )(document);

    const tableDocuments: ArkTableDocument[] = documents.map(document => {
        const { entityA, entityAName, entityAMyCompany, entityAOnWatchlist, entityB, entityBName, entityBMyCompany, entityBOnWatchlist, isAgency, agencyEntityId, principalEntities } = document;

        let entityAInfo: ArkTableDocumentEntity = {
            entityId: entityA,
            entityName: entityAName,
            myCompanyEntity: entityAMyCompany,
            entityOnWatchlist: entityAOnWatchlist
        };
        let entityBInfo: ArkTableDocumentEntity = {
            entityId: entityB,
            entityName: entityBName,
            myCompanyEntity: entityBMyCompany,
            entityOnWatchlist: entityBOnWatchlist
        };
        if (isAgency && agencyEntityId) {
            const partyAIsAgent = entityA === agencyEntityId;
            if (partyAIsAgent) {
                entityAInfo = { ...entityAInfo, isAgentEntity: true, principalEntities };
            } else {
                entityBInfo = { ...entityBInfo, isAgentEntity: true, principalEntities };
            }
        }
        const strippedDocument = stripDocumentEntities(document);
        return { ...strippedDocument, entityA: entityAInfo, entityB: entityBInfo };
    });

    return tableDocuments;
};

export const getOriginalDocuments = (state: ApplicationState): ArkDocument[] => getMyDocumentsRoot(state).availableOriginalDocuments;
export const getBaseDocuments = (state: ApplicationState): ArkDocument[] => getMyDocumentsRoot(state).availableBaseDocuments;
export const getAgreementTypeDocuments = (state: ApplicationState): ArkDocument[] => getMyDocumentsRoot(state).agreementTypeDocuments;

export const getAnalysisUploadDocument = (state: ApplicationState): UploadDocument => getMyDocumentsRoot(state).analysisUploadDocument;

export const getOriginalDocumentUrl = (state: ApplicationState): string | null => getMyDocumentsRoot(state).originalDocumentUrl;

export const getSecondaryDocumentUrl = (state: ApplicationState): string | null => getMyDocumentsRoot(state).secondaryDocumentUrl;

export const getDocumentInView = (state: ApplicationState): null | SelectedDocument => getMyDocumentsRoot(state).documentInView;

export const getDocumentUrl = (state: ApplicationState): string | null => {
    const documentInView = getDocumentInView(state);
    if (documentInView === SelectedDocument.SUPPORT) {
        return getSupportDocumentUrl(state);
    }
    return documentInView === SelectedDocument.SECONDARY ? getSecondaryDocumentUrl(state) : getOriginalDocumentUrl(state);
};

export const getOriginalDocument = (state: ApplicationState): ArkDocument | null => getMyDocumentsRoot(state).originalDocument;

export const getDocumentLinkDetails = (state: ApplicationState): DocumentLinkDetails | null => {
    const selectedDocument = getOriginalDocument(state);
    if (!selectedDocument) {
        return null;
    }
    const { entityA, entityB, documentNameId, documentId } = selectedDocument;
    return {
        entityA,
        entityB,
        documentNameId,
        documentId
    };
};

export const getIsAgencyDocument = (state: ApplicationState): boolean => {
    const originalDocument = getOriginalDocument(state);
    return !!originalDocument && !!originalDocument.isAgency;
};

export const getIsSecondaryAnalyzing = (state: ApplicationState): boolean => getMyDocumentsRoot(state).secondaryDocumentAnalyzing;

export const getSecondaryDocument = (state: ApplicationState): ArkDocument | null => getMyDocumentsRoot(state).secondaryDocument;

export const getIsOpening = (state: ApplicationState): boolean => getMyDocumentsRoot(state).isOpening;

export const getNewDocumentId = (state: ApplicationState): number | null => getMyDocumentsRoot(state).newDocumentId;

export const getOriginalDocumentsModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).originalDocumentsModalOpen;

export const getBaseDocumentsModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).baseDocumentsModalOpen;

export const getDocumentLinkModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).documentLinkModalOpen;

export const getLinkedDocumentsModalOpen = (state: ApplicationState): number | null => getMyDocumentsRoot(state).linkDocumentsModalOpen;

export const getIncompleteDocumentUrl = (state: ApplicationState): string | null => getMyDocumentsRoot(state).incompleteDocumentUrl;

export const getIncompleteDocument = (state: ApplicationState): ArkDocument | null => getMyDocumentsRoot(state).incompleteDocument;

export const getIncompleteDocumentDetailsUpdated = (state: ApplicationState): boolean => getMyDocumentsRoot(state).incompleteDocumentDetailsUpdated;

export const getAllIncompleteDocuments = (state: ApplicationState): ArkDocument[] => getMyDocumentsRoot(state).incompleteDocuments;

export const getSupportDocuments = (state: ApplicationState): LinkedDocument[] => {
    const originalDocument = getOriginalDocument(state);
    const secondaryDocument = getSecondaryDocument(state);
    if (secondaryDocument) {
        return secondaryDocument.supportDocuments!;
    }
    return originalDocument?.supportDocuments || [];
};

export const getLinkedDocuments = (state: ApplicationState): LinkedDocument[] | null => getMyDocumentsRoot(state).linkedDocuments;

export const getSelectedSupportDocument = (state: ApplicationState): LinkedDocument | null => getMyDocumentsRoot(state).supportDocument;

export const getSupportDocumentUrl = (state: ApplicationState): string | null => getMyDocumentsRoot(state).supportDocumentUrl;

export const getDocumentAnalysisView = (state: ApplicationState): AnalysisView => getMyDocumentsRoot(state).documentAnalysisView;

export const getOriginalDocumentToDelete = (state: ApplicationState): ArkDocument | ArkTableDocument | null => getMyDocumentsRoot(state).originalDocumentToDelete;

export const getDeleteDocumentModalOpen = (state: ApplicationState): boolean => !isNull(getOriginalDocumentToDelete(state));

export const getIsDeleting = (state: ApplicationState): boolean => getMyDocumentsRoot(state).isDeleting;

export const getOriginalDocumentToRename = (state: ApplicationState): ArkTableDocument | null => getMyDocumentsRoot(state).renameDocument;

export const getRenameDocumentModalOpen = (state: ApplicationState): boolean => !isNull(getOriginalDocumentToRename(state));

export const getDocumentDescriptionsHaveUpdated = (state: ApplicationState): boolean => getMyDocumentsRoot(state).documentDescriptionsHaveUpdated;

export const getIsRenaming = (state: ApplicationState): boolean => getMyDocumentsRoot(state).isRenaming;

export const getMLCompletedModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).mlCompletedModalOpen;

export const getIncompleteMLData = (state: ApplicationState): MLData | null => getMyDocumentsRoot(state).incompleteDocumentMLData;

export const getIsCreatingEntity = (state: ApplicationState): boolean => getMyDocumentsRoot(state).isCreatingEntity;

export const getIncompleteScheduleStartPage = (state: ApplicationState): number | null => getMyDocumentsRoot(state).incompleteScheduleStartPage;

export const getDocumentAnalysisSelectedPage = (state: ApplicationState): number | null => getMyDocumentsRoot(state).documentAnalysisSelectedPage;

export const getScheduleStartPagePlaceholder = (state: ApplicationState): number | null => getMyDocumentsRoot(state).scheduleStartPlaceholder;

export const getScheduleEndPagePlaceholder = (state: ApplicationState): number | null => getMyDocumentsRoot(state).scheduleEndPlaceholder;

export const getCdmJsonPreview = (state: ApplicationState): object | null => getMyDocumentsRoot(state).cdmJson;

export const getAllPreExecutionDocuments = (state: ApplicationState): ArkTableDocument[] => getMyDocumentsRoot(state).preExecutionDocuments;

export const getDocumentFetchAgreementTypeId = (state: ApplicationState): number | null => getMyDocumentsRoot(state).fetchDocumentsAgreementTypeId;

export const getAmendmentInformation = (state: ApplicationState): ClientAmendmentDocumentQuery => getMyDocumentsRoot(state).amendmentInformation;

export const getFilteredExtensiveQueries = (state: ApplicationState): DocumentQuery[] => getMyDocumentsRoot(state).filteredExtensiveQueries;
export const getSuggestedAndRemainingQueries = (state: ApplicationState): SuggestedAndRemainingQueries => {
    const amendmentInformation = getAmendmentInformation(state);
    const filteredExtensiveQueries = getFilteredExtensiveQueries(state);
    const suggestedQueries = filteredExtensiveQueries.filter(({ key }) => amendmentInformation.suggestedQueries.includes(key));
    const remainingQueries = filteredExtensiveQueries.filter(({ key }) => !amendmentInformation.suggestedQueries.includes(key));
    return { suggestedQueries, remainingQueries };
};
export const getParentDatasetInstanceIsDraft = (state: ApplicationState): boolean => getMyDocumentsRoot(state).parentDatasetInstanceIsDraft;

export const getAmendmentStartPage = (state: ApplicationState): number | undefined => getMyDocumentsRoot(state).amendmentStartPage;
export const getAmendmentEndPage = (state: ApplicationState): number | undefined => getMyDocumentsRoot(state).amendmentEndPage;
export const getExtractWithAI = (state: ApplicationState): boolean => getMyDocumentsRoot(state).extractWithAI;

export const getSelectedDocumentId = (state: ApplicationState): number | null => {
    const documentInView = getDocumentInView(state);
    if (documentInView === SelectedDocument.SUPPORT) {
        return getSelectedSupportDocument(state)?.documentId || null;
    }
    if (documentInView === SelectedDocument.SECONDARY) {
        return getSecondaryDocument(state)?.documentId || null;
    }
    return getOriginalDocument(state)?.documentId || null;
};

export const getOriginalDocumentId = (state: ApplicationState): number | null => getOriginalDocument(state)?.documentId || null;

export const getDocumentNettingAnalysisModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).documentNettingAnalysisModalOpen;

export const getColumnConfigurationModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).columnConfigurationModalOpen;

export const getAIDismissedCheckbox = (state: ApplicationState): boolean => getMyDocumentsRoot(state).aiDismissedCheckbox;

export const getAIConfirmationModalVisible = (state: ApplicationState): boolean => {
    const isOpen = getMLCompletedModalOpen(state);
    const user = getUser(state);
    return isOpen && !isNull(user) && user.showAIConfirmationModal;
};

// Table Filters and Pagination
export const getOriginalDocumentsPageNumber = (state: ApplicationState): number => getMyDocumentsRoot(state).originalDocumentPage;
export const getTotalOriginalDocuments = (state: ApplicationState): number => getMyDocumentsRoot(state).totalOriginalDocuments;
export const getOriginalDocumentFilters = (state: ApplicationState): TableFilters => getMyDocumentsRoot(state).originalDocumentFilters;
export const getOriginalDocumentColumnSort = (state: ApplicationState): ColumnSort | undefined => getMyDocumentsRoot(state).originalDocumentSort;
export const getOriginalDocumentPageSize = (state: ApplicationState): number => getMyDocumentsRoot(state).originalDocumentPageSize;

export const getBaseDocumentsPageNumber = (state: ApplicationState): number => getMyDocumentsRoot(state).baseDocumentPage;
export const getTotalBaseDocuments = (state: ApplicationState): number => getMyDocumentsRoot(state).totalBaseDocuments;
export const getBaseDocumentFilters = (state: ApplicationState): TableFilters => getMyDocumentsRoot(state).baseDocumentFilters;
export const getBaseDocumentColumnSort = (state: ApplicationState): ColumnSort | undefined => getMyDocumentsRoot(state).baseDocumentSort;
export const getBaseDocumentPageSize = (state: ApplicationState): number => getMyDocumentsRoot(state).baseDocumentPageSize;

export const getAgreementTypeDocumentsPageNumber = (state: ApplicationState): number => getMyDocumentsRoot(state).agreementTypeDocumentPage;
export const getTotalAgreementTypeDocuments = (state: ApplicationState): number => getMyDocumentsRoot(state).totalAgreementTypeDocuments;
export const getAgreementTypeDocumentFilters = (state: ApplicationState): TableFilters => getMyDocumentsRoot(state).agreementTypeDocumentFilters;
export const getAgreementTypeDocumentColumnSort = (state: ApplicationState): ColumnSort | undefined => getMyDocumentsRoot(state).agreementTypeDocumentSort;
export const getAgreementTypeDocumentPageSize = (state: ApplicationState): number => getMyDocumentsRoot(state).agreementTypeDocumentPageSize;

export const getIncompleteDocumentsPageNumber = (state: ApplicationState): number => getMyDocumentsRoot(state).incompleteDocumentPage;
export const getTotalIncompleteDocuments = (state: ApplicationState): number => getMyDocumentsRoot(state).totalIncompleteDocuments;
export const getIncompleteDocumentFilters = (state: ApplicationState): TableFilters => getMyDocumentsRoot(state).incompleteDocumentFilters;
export const getIncompleteDocumentColumnSort = (state: ApplicationState): ColumnSort | undefined => getMyDocumentsRoot(state).incompleteDocumentSort;
export const getIncompleteDocumentPageSize = (state: ApplicationState): number => getMyDocumentsRoot(state).incompleteDocumentPageSize;

export const getPreExecutionDocumentsPageNumber = (state: ApplicationState): number => getMyDocumentsRoot(state).preExecutionDocumentPage;
export const getTotalPreExecutionDocuments = (state: ApplicationState): number => getMyDocumentsRoot(state).totalPreExecutionDocuments;
export const getPreExecutionDocumentFilters = (state: ApplicationState): TableFilters => getMyDocumentsRoot(state).preExecutionDocumentFilters;
export const getPreExecutionDocumentColumnSort = (state: ApplicationState): ColumnSort | undefined => getMyDocumentsRoot(state).preExecutionDocumentSort;
export const getPreExecutionDocumentPageSize = (state: ApplicationState): number => getMyDocumentsRoot(state).preExecutionDocumentPageSize;

// Custom Filters
export const getDocumentCustomFiltersModalOpen = (state: ApplicationState): boolean => getMyDocumentsRoot(state).documentCustomFiltersModalOpen;
export const getSelectedDocumentCustomFilterId = (state: ApplicationState): string | null => getMyDocumentsRoot(state).selectedDocumentCustomFilterId;
export const getDocumentCustomFilterHasUpdated = (state: ApplicationState): boolean => getMyDocumentsRoot(state).documentCustomFilterHasUpdated;
export const getSelectedCustomFilterName = (state: ApplicationState): string => {
    const allDocumentFilters = getAllDocumentCustomFilters(state);
    const selectedId = getSelectedDocumentCustomFilterId(state);
    return allDocumentFilters.find(({ id }) => id === selectedId)?.filterName || '';
};
